import React, { useState, useEffect } from 'react';
import {
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

const StripePayment = ({
  onPaymentComplete,
  createIntent,
  verifyPayment,
  buttonText = 'Pay Now',
  amount = 0,
  currency = 'GBP'
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initIntent = async () => {
      try {
        setLoading(true);
        const secret = await createIntent();
        setClientSecret(secret);
      } catch (err) {
        setError('Failed to initialize payment: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };

    initIntent();
  }, [createIntent]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      }
    });

    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
    } else {
      if (paymentIntent.status === 'succeeded') {
        try {
          const success = await verifyPayment(paymentIntent.id);
          if (success) {
            onPaymentComplete({ success: true, paymentIntentId: paymentIntent.id });
          } else {
            setError('Payment succeeded but verification failed.');
            setProcessing(false);
          }
        } catch (verifyErr) {
          setError('Verification error: ' + (verifyErr.response?.data?.message || verifyErr.message));
          setProcessing(false);
        }
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        Card Details
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{
        p: 2,
        border: '1px solid',
        borderColor: '#e0e0e0',
        borderRadius: 1,
        mb: 3,
        backgroundColor: '#ffffff'
      }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>

      <Button
        type="submit"
        disabled={!stripe || processing}
        variant="contained"
        fullWidth
        size="large"
        sx={{
          bgcolor: 'secondary.main',
          color: 'secondary.contrastText',
          fontWeight: 700,
          py: 1.5,
          '&:hover': { bgcolor: 'secondary.dark' },
          '&.Mui-disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled'
          }
        }}
      >
        {processing ? <CircularProgress size={24} color="inherit" /> : buttonText}
      </Button>

      <Typography variant="caption" color="textSecondary" align="center" display="block" sx={{ mt: 2 }}>
        Secure payment powered by Stripe. Your card data is never stored on our servers.
      </Typography>
    </Box>
  );
};

export default StripePayment;
